import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type NavigationGuardNext
} from 'vue-router'
import { authGuard } from '@auth0/auth0-vue'
import AppView from '@/views/AppView.vue'
import CallbackView from '@/views/CallbackView.vue'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import auth0Plugin from '@/setups/authSetup'
import { tracker } from '@/tracker'

import DashboardView from '@/views/app/DashboardView.vue'
import NewDevelopmentView from '@/views/app/NewDevelopmentView.vue'
import PropertyReportEditView from '@/views/app/PropertyReports/PropertyReportEditView.vue'
import PropertyReportNewView from '@/views/app/PropertyReports/PropertyReportNewView.vue'
import PropertyReportOverviewView from '@/views/app/PropertyReports/PropertyReportOverviewView.vue'
import PropertyReportsSampleView from '@/views/app/PropertyReports/PropertyReportsSampleView.vue'
import PropertyReportView from '@/views/app/PropertyReports/PropertyReportView.vue'
import PropertyReportByTagView from '@/views/app/PropertyReports/PropertyReportByTagView.vue'
import SubscribePropertyReportsView from '@/views/app/SubscribePropertyReportsView.vue'

import MarketReportsView from '@/views/app/MarketReportsView.vue'
import MarketReportsSampleView from '@/views/app/MarketReports/MarketReportsSampleView.vue'
import MarketReportView from '@/views/app/MarketReports/MarketReportView.vue'
import MarketReportNewView from '@/views/app/MarketReports/MarketReportNewView.vue'
import MarketReportEditView from '@/views/app/MarketReports/MarketReportEditView.vue'
import SubscribeMarketReportsView from '@/views/app/SubscribeMarketReportsView.vue'

import SettingsView from '@/views/SettingsView.vue'
import SuccessView from '@/views/SuccessView.vue'

import Profile from '@/views/settings/Profile.vue'
import Organization from '@/views/settings/Organization.vue'
import Billing from '@/views/settings/Billing.vue'

import { FeatureFlag, propertyReportSamples, marketReportSamples } from 'common'

const authBeforeEnter = async (
  to: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  await authGuard(to)
  const user = auth0Plugin.user.value
  user?.email_verified === false ? next({ name: 'Verify Email' }) : next()
}

const propertyReportsOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { hasPropertyReports } = storeToRefs(useStripeStore())
  hasPropertyReports.value ? next() : next({ path: '/' })
}

const marketReportsOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { hasMarketReports } = storeToRefs(useStripeStore())
  hasMarketReports.value ? next() : next({ path: '/' })
}

const propertyReportsOrLimitedAccessBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { hasPropertyReports, hasLimitedAccess } = storeToRefs(useStripeStore())
  hasPropertyReports.value || hasLimitedAccess.value ? next() : next({ path: '/' })
}

const bulkDataOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { hasBulkData } = storeToRefs(useStripeStore())
  hasBulkData.value ? next() : next({ path: '/' })
}

const helloDataEmployeeOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  useUserStore().isHelloDataEmployee ? next() : next({ path: '/' })
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/property-reports',
      redirect: '/'
    },
    {
      path: '/',
      component: AppView,
      beforeEnter: authBeforeEnter,
      children: [
        {
          path: '/',
          name: 'Dashboard',
          component: DashboardView
        },
        {
          path: '/subscribe/property-reports',
          name: 'Subscribe to Property Reports',
          component: SubscribePropertyReportsView
        },
        ...propertyReportSamples.map((sample, i) => ({
          path: `/property-reports/sample/${i}`,
          name: sample.name,
          props: () => ({ sample }),
          component: PropertyReportsSampleView
        })),
        {
          path: '/property-reports/public/:reportId',
          name: 'Public Property Report',
          component: PropertyReportOverviewView,
          props: (route) => ({ reportId: route.params.reportId })
        },
        {
          path: '/property-reports/new',
          name: 'New Property Report',
          props: (route) => ({ propertyId: route.query.propertyId }),
          component: PropertyReportNewView,
          beforeEnter: propertyReportsOnlyBeforeEnter
        },
        {
          path: '/property-reports/:reportId',
          name: 'Property Report',
          props: true,
          component: PropertyReportView,
          beforeEnter: propertyReportsOrLimitedAccessBeforeEnter
        },
        {
          path: '/property-reports/:reportId/edit',
          name: 'Edit Property Report',
          props: true,
          component: PropertyReportEditView,
          beforeEnter: propertyReportsOnlyBeforeEnter
        },
        {
          path: '/property-reports-by-tag',
          name: 'Tag Report',
          component: PropertyReportByTagView,
          beforeEnter: propertyReportsOnlyBeforeEnter
        },
        {
          path: '/success',
          name: 'Thank You',
          component: SuccessView
        },
        {
          path: '/:catchAll(.*)',
          redirect: '/'
        }
      ]
    },
    {
      path: '/regional-reports',
      component: AppView,
      beforeEnter: authBeforeEnter,
      children: [
        {
          path: '/regional-reports',
          name: 'Regional Reports',
          component: MarketReportsView,
          beforeEnter: propertyReportsOnlyBeforeEnter
        },
        {
          path: '/subscribe/regional-reports',
          name: 'Subscribe to Regional Reports',
          component: SubscribeMarketReportsView,
          beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
        },
        ...marketReportSamples.map((sample, i) => ({
          path: `/regional-reports/sample/${i}`,
          name: sample.name,
          props: () => ({ sample }),
          component: MarketReportsSampleView,
          beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
        })),
        {
          path: '/regional-reports/new',
          name: 'New Regional Report',
          component: MarketReportNewView,
          beforeEnter: marketReportsOnlyBeforeEnter
        },
        {
          path: '/regional-reports/:reportId',
          name: 'Regional Report',
          props: true,
          component: MarketReportView,
          beforeEnter: marketReportsOnlyBeforeEnter
        },
        {
          path: '/regional-reports/:reportId/edit',
          name: 'Edit Regional Report',
          props: true,
          component: MarketReportEditView,
          beforeEnter: marketReportsOnlyBeforeEnter
        }
      ]
    },
    {
      path: '/',
      component: AppView,
      beforeEnter: authBeforeEnter,
      children: [
        {
          path: '/datasets',
          children: [
            {
              path: '',
              name: 'Datasets',
              component: () => import('@/views/app/Datasets/DatasetDashboardView.vue')
            },
            {
              path: 'new',
              name: 'Query Builder',
              component: () => import('@/views/app/Datasets/DatasetBuilderView.vue')
            },
            {
              path: ':datasetId',
              name: 'Dataset Viewer',
              component: () => import('@/views/app/Datasets/DatasetBuilderView.vue'),
              props: (route) => ({
                // Convert to number and validate
                datasetId: Number.isInteger(Number(route.params.datasetId))
                  ? Number(route.params.datasetId)
                  : null
              })
            }
          ],
          beforeEnter: (to, from, next) => {
            const hasQueryBuilder = useUserStore().hasQueryBuilder
            if (hasQueryBuilder) {
              next()
              return
            }

            next({ path: '/' })
          }
        },
        {
          path: '/similar-markets',
          name: 'Similar Markets',
          component: () => import('@/views/app/MarketReports/SimilarMarketsView.vue'),
          beforeEnter: (to, from, next) => {
            const hasFeatureFlag = useUserStore().hasFeatureFlag(null, FeatureFlag.MARKET_FINDER)
            if (hasFeatureFlag) {
              next()
              return
            }

            next({ path: '/' })
          }
        },
        {
          path: '/new-development',
          name: 'New Development',
          props: (route) => ({
            gotoEditNext: route.query.gotoEditNext || null,
            existingPropertyId: route.query.existingPropertyId || null,
            gotoReportIdNext: route.query.gotoReportIdNext || null
          }),
          component: NewDevelopmentView,
          beforeEnter: propertyReportsOnlyBeforeEnter
        },
        {
          path: '/bulk-rental-property-data/edit/:bulkDataId',
          name: 'Edit Bulk Rental Property Data',
          props: true,
          component: () => import('@/views/app/BulkDataEditView.vue'),
          beforeEnter: bulkDataOnlyBeforeEnter
        },
        {
          path: '/settings',
          name: 'Settings',
          component: SettingsView,
          redirect: '/settings/profile',
          children: [
            {
              path: 'profile',
              name: 'Profile',
              component: Profile
            },
            {
              path: 'organization/:urlTab?',
              name: 'Organization',
              props: true,
              component: Organization
            },
            {
              path: 'admin',
              component: () => import('@/views/settings/Admin.vue'),
              beforeEnter: helloDataEmployeeOnlyBeforeEnter,
              children: [
                {
                  path: '',
                  name: 'Admin',
                  component: () => import('@/views/admin/Dashboard.vue')
                },
                {
                  path: 'new-pilot',
                  name: 'New Pilot',
                  component: () => import('@/views/admin/NewPilot.vue')
                },
                {
                  path: 'new-client',
                  name: 'New Client',
                  component: () => import('@/views/admin/NewClient.vue')
                },
                {
                  path: 'all-users',
                  name: 'All Users',
                  component: () => import('@/views/admin/AllUsers.vue')
                },
                {
                  path: 'all-users/:userId',
                  name: 'Admin User',
                  props: true,
                  component: () => import('@/views/admin/AdminUser.vue')
                },
                {
                  path: 'copy-report',
                  name: 'Copy Report',
                  props: (route) => ({ reportId: route.query.reportId }),
                  component: () => import('@/views/admin/CopyReport.vue')
                }
              ]
            },
            {
              path: 'billing',
              name: 'Billing',
              component: Billing
            }
          ]
        }
      ]
    },
    {
      path: '/verify-email',
      name: 'Verify Email',
      beforeEnter: async (
        to: RouteLocationNormalized,
        __: RouteLocationNormalized,
        next: NavigationGuardNext
      ) => {
        await authGuard(to)
        const user = auth0Plugin.user.value
        if (user?.email_verified === true) {
          next({ name: 'Dashboard' })
          return
        }
        next()
      },
      component: () => import('@/views/VerifyEmailView.vue')
    },
    {
      path: '/callback',
      component: CallbackView
    }
  ]
})

router.afterEach((to) => {
  const appTitle = import.meta.env.VITE_APP_TITLE
  let pageName = to.name?.toString()
  if (to.query['tag']) {
    pageName = `Tag Report`
  }
  document.title = to.name ? `${appTitle} | ${pageName}` : appTitle
  if (pageName !== undefined) {
    tracker.pageView(pageName, to.path)
  } else {
    console.warn('No name for route', to)
  }
})

export default router
